import { redirect } from "@tanstack/react-router";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { Coins, ShieldCheck, X } from "lucide-react";
import { getWallet } from "@/lib/partner.functions";
import { RewardsSections } from "@/components/partner/RewardsSections";
import { RewardShop } from "@/components/partner/RewardShop";
import { StatusPill } from "@/components/StatusPill";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { paymentsConfigured } from "@/lib/stripe";
import { gbp, sc, ukDateTime } from "@/lib/format";
import { AppHeader } from "@/components/site/AppHeader";
import { stripePriceIdForPack } from "@/lib/credit-packs";

const walletQuery = queryOptions({ queryKey: ["wallet"], queryFn: () => getWallet() });


export const Route = createFileRoute("/_authenticated/partner/wallet")({
  // Retired marketplace screen: hidden from visitors, data kept.
  beforeLoad: () => { throw redirect({ to: "/guides" }); },
  head: () => ({
    meta: [
      { title: "Shields Credits wallet — Shields" },
      { name: "description", content: "Your Shields Credits balance, packs and history." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: WalletPage,
});

function WalletPage() {
  const { data } = useSuspenseQuery(walletQuery);
  const queryClient = useQueryClient();
  const { openCheckout, closeCheckout, checkoutElement } = useStripeCheckout();
  const configured = paymentsConfigured();

  async function buyPack(slug: string) {
    if (!configured) return;
    openCheckout({
      priceId: stripePriceIdForPack(slug) ?? slug,
      returnUrl: `${window.location.origin}/partner/wallet`,
    });
  }

  function onCheckoutComplete() {
    closeCheckout();
    queryClient.invalidateQueries({ queryKey: ["wallet"] });
  }

  return (
    <div className="min-h-screen bg-background pb-28">
      {checkoutElement && <PaymentTestModeBanner />}
      <AppHeader workspace="partner" />

      <main className="product-page layout-credits-market section-pad mx-auto py-7 sm:py-10">
        <h1 className="flex items-center gap-3 text-2xl font-bold sm:text-3xl">
          <Coins className="h-7 w-7 text-primary" aria-hidden /> Shields Credits
        </h1>

        <div className="relative mt-5 overflow-hidden rounded-[20px] border border-[#dce6ef] bg-white p-5 sm:p-6">
          
          <div className="relative flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="flex items-center gap-2 text-sm font-bold text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden /> Available balance
              </p>
              <p className="mt-2 font-display text-4xl font-extrabold tracking-tight text-[#07164f]">{sc(data.balance)}</p>
              <p className="mt-2 text-sm font-medium text-white/70">
                Use Credits when a bid is selected or when you take a Get Now job.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-primary">Fair-use promise</p>
              <p className="mt-1 text-sm font-semibold">Browsing and unsuccessful bids cost 0 SC</p>
            </div>
          </div>
          <p className="relative mt-6 max-w-2xl border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
            Shields Credits are used only for acquiring work through the marketplace. They are not cash, cannot be withdrawn, and are not shown to customers as money.
          </p>
        </div>

        <section className="mt-10">
          <h2 className="font-display text-lg font-bold">Buy credits</h2>
          {checkoutElement ? (
            <div className="card-surface mt-4 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display font-bold">Secure checkout</h3>
                <button
                  onClick={onCheckoutComplete}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Close checkout"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {checkoutElement}
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Close this panel to return to your wallet. Credits appear here once payment is
                confirmed.
              </p>
            </div>
          ) : data.packs.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">
              Credit packs are being configured. You can still earn free credits through verified
              achievements.
            </p>
          ) : (
            <div className="credit-pack-grid">
              {data.packs.map((p) => (
                <div key={p.id} className="credit-pack-card">
                  <div className="credit-pack-card__amount">
                    <span>SHIELDS CREDITS</span>
                    <strong>{sc(p.credits)}</strong>
                  </div>
                  <div className="credit-pack-card__price">
                    <span>ONE-OFF PRICE</span>
                    <strong>{gbp(p.price_pence)}</strong>
                  </div>
                  <div className="credit-pack-card__value">
                    <p className="font-bold text-foreground">For marketplace wins and Get Now</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      Credits are added only after Stripe confirms the payment.
                    </p>
                  </div>
                  <button
                    onClick={() => buyPack(p.slug)}
                    disabled={!configured}
                    className="credit-pack-card__cta"
                  >
                    {configured ? `Buy ${sc(p.credits)} · ${gbp(p.price_pence)}` : "Payments not connected yet"}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            We never simulate a purchase. Credits are added only after the card payment is confirmed
            by our payment provider.
          </p>
        </section>

        {data.purchases.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-lg font-bold">Purchases</h2>
            <ul className="mt-4 space-y-2">
              {data.purchases.map((p) => (
                <li
                  key={p.id}
                  className="card-surface flex items-center justify-between p-4 text-sm"
                >
                  <span>
                    {sc(p.credits)} · {gbp(p.price_pence)}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {ukDateTime(p.created_at)}
                    </span>
                    <StatusPill status={p.status} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <RewardsSections />

        <RewardShop />

        <section className="mt-10">
          <h2 className="font-display text-lg font-bold">Credit history</h2>
          {data.ledger.length === 0 ? (
            <p className="card-surface mt-4 p-6 text-sm text-muted-foreground">
              No credit activity yet.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.ledger.map((entry) => (
                <li
                  key={entry.id}
                  className="card-surface flex items-center justify-between p-4 text-sm"
                >
                  <div>
                    <p className="font-semibold capitalize">{entry.reason.replaceAll("_", " ")}</p>
                    <p className="text-xs text-muted-foreground">
                      {ukDateTime(entry.created_at)} · {entry.source}
                    </p>
                  </div>
                  <span
                    className={`font-display font-extrabold ${entry.amount >= 0 ? "text-success" : "text-destructive"}`}
                  >
                    {entry.amount >= 0 ? "+" : ""}
                    {entry.amount} SC
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
